import { ReactElement, useCallback, useMemo } from 'react'
import { find } from 'lodash-es'
import { Box } from '@mui/material'
import usePlansQuery from '@editor/pages/Plans/hooks/usePlansQuery'
import { EmptyState } from '@ui2/feedback'
import { useEditorViewStore } from '@editorStores'
import { useBlockAction } from '@editorHooks'
import { Viewer, ViewerDocument } from '@editorComponents'
import { usePlanPdfViewer } from '../../PlanPdfViewerContext'
import { PLAN_VIEW_PLAN_PDF_BLOCK_ACTION } from '../../constants'
import PlanViewLoadPlanNotice from '../PlanViewLoadPlanNotice'
import PlanViewSceneOverlay from '../PlanViewSceneOverlay'

/** Blank strip the WebViewer UI draws along its left edge; hidden under the nav rail. */
const VIEWER_GUTTER_WIDTH = 8

/**
 * The plan view's scene overlay. While the "Pläne" tab is open, covers the scene (right of the nav
 * rail) with a PDF preview of the plan highlighted in the list. The PDF can be edited
 * (annotations); the row's expanded panel then offers "Speichern" / "Verwerfen". The scene stays
 * mounted underneath, so switching back to "Planansicht" restores it without reloading. On the
 * "Planansicht" tab it only shows the "Lastplan" notice (when that plan type is chosen).
 */
const PlanPdfPreview = (): ReactElement | null => {
  const tab = useEditorViewStore(state => state.planView.tab)
  const selectedPlanGuid = useEditorViewStore(state => state.planView.selectedPlanGuid)
  const { data: plans } = usePlansQuery()
  const { setViewer, changedDocument, setChangedDocument, loadedDocument, setLoadedDocument } =
    usePlanPdfViewer()
  // Unsaved PDF changes block leaving the tab/page (the viewer and its edits would be gone).
  useBlockAction(PLAN_VIEW_PLAN_PDF_BLOCK_ACTION, changedDocument ? 'provider' : undefined)

  const selectedPlan = selectedPlanGuid ? find(plans, { guid: selectedPlanGuid }) : undefined

  // Keep the url the selected plan was loaded from: the plan list hands out freshly signed urls on
  // every refetch (e.g. after a Kommentar/Tags autosave), and a new url reloads the viewer, which
  // would lose the view position and drop unsaved changes.
  const pinnedUrl =
    changedDocument?.guid === selectedPlan?.guid
      ? changedDocument?.url
      : loadedDocument?.guid === selectedPlan?.guid
      ? loadedDocument?.url
      : undefined
  const documentUrl = selectedPlan ? pinnedUrl ?? selectedPlan.url : undefined

  const selectedPlanName = selectedPlan?.node_name
  const document = useMemo<ViewerDocument | null>(
    () =>
      selectedPlanGuid && selectedPlanName !== undefined && documentUrl
        ? {
            guid: selectedPlanGuid,
            name: selectedPlanName,
            url: documentUrl,
            fileType: 'application/pdf',
          }
        : null,
    [selectedPlanGuid, selectedPlanName, documentUrl],
  )

  // Records the document (plan + url) the preview loaded / the user edited; see `documentUrl`.
  const onDocumentLoaded = useCallback(() => {
    if (document) setLoadedDocument({ guid: document.guid, url: document.url })
  }, [document, setLoadedDocument])

  const onAnnotationsChanged = useCallback(() => {
    if (document) setChangedDocument({ guid: document.guid, url: document.url })
  }, [document, setChangedDocument])

  if (tab !== 'plans') return <PlanViewLoadPlanNotice />

  return (
    <PlanViewSceneOverlay railUnderlap={VIEWER_GUTTER_WIDTH} data-cy="plan-view-pdf-preview">
      {/* Mounted for the whole tab (not only with a plan selected): the rows' PDF/A conversion
          runs on this viewer's PDFNet engine. */}
      <Viewer
        ref={setViewer}
        document={document}
        // Same height as the Pläne page's viewer: the scene pane spans the editor below the navbar.
        viewerHeight="calc(100vh - 58px)"
        hideFileActions
        onDocumentLoaded={onDocumentLoaded}
        onAnnotationsChanged={onAnnotationsChanged}
      />
      {!document && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'common.white',
          }}
        >
          <EmptyState>
            {selectedPlan
              ? 'Das PDF dieses Plans ist nicht verfügbar'
              : 'Plan in der Liste auswählen, um die Vorschau anzuzeigen'}
          </EmptyState>
        </Box>
      )}
    </PlanViewSceneOverlay>
  )
}

export default PlanPdfPreview
